(() => {
  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  const findSiteHeader = (root) => {
    if (root instanceof Element && root.matches('[data-sb-site-header]')) return root;
    return root.querySelector?.('[data-sb-site-header]') || null;
  };

  const initHeader = (root = document) => {
    const siteHeader = findSiteHeader(root);
    if (!siteHeader) return;

    siteHeader.sbHeaderController?.abort();
    const controller = new AbortController();
    const { signal } = controller;
    siteHeader.sbHeaderController = controller;

    const header = siteHeader.querySelector('[data-sb-header]');
    const search = siteHeader.querySelector('[data-sb-search]');
    const searchOpen = siteHeader.querySelector('[data-sb-search-open]');
    const searchClose = siteHeader.querySelector('[data-sb-search-close]');
    const mobile = siteHeader.querySelector('[data-sb-mobile]');
    const mobileOpen = siteHeader.querySelector('[data-sb-mobile-open]');
    const mobileClosers = siteHeader.querySelectorAll('[data-sb-mobile-close]');
    const overlay = siteHeader.querySelector('.sb-mobile-overlay');
    let searchReturnFocus = null;
    let mobileReturnFocus = null;

    const updateBodyLock = () => {
      const panelIsOpen = search?.classList.contains('is-open') || mobile?.classList.contains('is-open');
      document.body.classList.toggle('sb-lock', Boolean(panelIsOpen));
    };

    const updateHeader = () => {
      if (!header) return;
      header.classList.toggle('is-sticky', window.scrollY > 76);
    };
    updateHeader();
    window.addEventListener('scroll', updateHeader, { passive: true, signal });

    const toggleMobile = (open, restoreFocus = true) => {
      if (!mobile) return;
      const wasOpen = mobile.classList.contains('is-open');
      if (open && search?.classList.contains('is-open')) toggleSearch(false);
      if (open && !wasOpen) mobileReturnFocus = document.activeElement;
      mobile.classList.toggle('is-open', open);
      overlay?.classList.toggle('is-open', open);
      mobile.setAttribute('aria-hidden', String(!open));
      mobile.inert = !open;
      mobileOpen?.setAttribute('aria-expanded', String(open));
      updateBodyLock();
      if (open) window.setTimeout(() => mobile.querySelector(focusableSelector)?.focus(), 230);
      else if (wasOpen && restoreFocus && mobileReturnFocus instanceof HTMLElement) mobileReturnFocus.focus();
    };

    const toggleSearch = (open) => {
      if (!search) return;
      const wasOpen = search.classList.contains('is-open');
      if (open && mobile?.classList.contains('is-open')) toggleMobile(false, false);
      if (open && !wasOpen) searchReturnFocus = document.activeElement;
      search.classList.toggle('is-open', open);
      search.setAttribute('aria-hidden', String(!open));
      search.inert = !open;
      searchOpen?.setAttribute('aria-expanded', String(open));
      updateBodyLock();
      if (open) window.setTimeout(() => search.querySelector('input[type="search"]')?.focus(), 230);
      else if (wasOpen && searchReturnFocus instanceof HTMLElement) searchReturnFocus.focus();
    };

    searchOpen?.addEventListener('click', () => toggleSearch(true), { signal });
    searchClose?.addEventListener('click', () => toggleSearch(false), { signal });
    mobileOpen?.addEventListener('click', () => toggleMobile(true), { signal });
    mobileClosers.forEach((closer) => closer.addEventListener('click', () => toggleMobile(false), { signal }));

    const mobileBreakpoint = window.matchMedia('(max-width: 1025px)');
    mobileBreakpoint.addEventListener(
      'change',
      (event) => {
        if (!event.matches) toggleMobile(false, false);
      },
      { signal },
    );

    document.addEventListener(
      'click',
      (event) => {
        siteHeader.querySelectorAll('[data-sb-mega][open]').forEach((details) => {
          if (!details.contains(event.target)) details.removeAttribute('open');
        });
      },
      { signal },
    );

    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape') {
          toggleSearch(false);
          toggleMobile(false);
          siteHeader.querySelectorAll('[data-sb-mega][open]').forEach((details) => details.removeAttribute('open'));
          return;
        }

        if (event.key !== 'Tab') return;
        const activePanel = search?.classList.contains('is-open')
          ? search
          : mobile?.classList.contains('is-open')
            ? mobile
            : null;
        if (!activePanel) return;

        const focusable = Array.from(activePanel.querySelectorAll(focusableSelector)).filter(
          (element) => element instanceof HTMLElement && !element.hidden && element.getClientRects().length > 0,
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      },
      { signal },
    );
  };

  const start = () => initHeader(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();

  document.addEventListener('shopify:section:load', (event) => initHeader(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    findSiteHeader(event.target)?.sbHeaderController?.abort();
    document.body.classList.remove('sb-lock');
  });
})();
